import React, { useState, useEffect } from 'react'
import { Form, Col, Spinner, Button } from 'react-bootstrap'
import constants from '../../../config/constants'
import { toPersonName, isValidPersonName } from '../../../utility/stringUtils'
import PhoneInput, { isPossiblePhoneNumber } from 'react-phone-number-input'
import { Formik } from 'formik'
import * as yup from 'yup'
import SignupFormSocialButtons from './signupFormSocialButtons'
import { firebaseAuth } from '../../../services/firebase'
import { toast } from 'react-toastify'
import { friendlyMessageFor } from '../../../utility/errorHelper'

export enum PhoneLoginSignupFormMode {
  signup = 'signup',
  login = 'login'
}

interface PhoneLoginSignupFormProps {
  mode: PhoneLoginSignupFormMode
  phoneSignupCallback?: (
    firstName: string,
    lastName: string,
    phonenumber: string
  ) => void
  phoneLoginCallback?: (userId: string) => void
  uiConfig?: firebaseui.auth.Config
}

const msg = constants.inlineError
const signupSchema = yup.object({
  firstName: yup
    .string()
    .required(msg.fieldRequired.replace('%s', 'First name'))
    .test('ValidPersonName', msg.auth.invalidFirstName, value => {
      return isValidPersonName(value)
    }),
  lastName: yup
    .string()
    .required(msg.fieldRequired.replace('%s', 'Last name'))
    .test('ValidPersonName', msg.auth.invalidLastName, value => {
      return isValidPersonName(value)
    }),
  phonenumber: yup
    .string()
    .required(msg.fieldRequired.replace('%s', 'Phonenumber'))
})

const enterCodeSchema = yup.object({
  code: yup.string().length(6, msg.auth.invalid6DigitCode)
})

const PhoneLoginSignupForm: React.FC<PhoneLoginSignupFormProps> = ({
  mode,
  phoneSignupCallback,
  phoneLoginCallback,
  uiConfig
}) => {
  const [isValidNumber, setIsValidNumber] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [formValues, setFormValues] = useState(() => {
    return {
      firstName: '',
      lastName: '',
      phonenumber: '+65'
    }
  })

  const [recaptchaVerifier, setRecaptchaVerifier] = useState<
    firebase.auth.RecaptchaVerifier | undefined
  >(undefined)
  const [confirmationResult, setConfirmationResult] = useState<
    firebase.auth.ConfirmationResult | undefined
  >(undefined)

  useEffect(() => {
    // Initialize recaptcha on component load
    setRecaptchaVerifier(
      new firebaseAuth.RecaptchaVerifier('submitButton', {
        size: 'invisible'
      })
    )
  }, [])

  const verifyPhone = (phonenumber: string) => {
    if (recaptchaVerifier && isValidNumber) {
      setIsLoading(true)

      // Workaround to re-enable login button once user cancelled recaptcha challenge
      setTimeout(() => {
        setIsLoading(false)
      }, 5000)

      // Below API call may cause recaptcha challenge to show up
      firebaseAuth()
        .signInWithPhoneNumber(phonenumber, recaptchaVerifier)
        .then(result => {
          setIsLoading(false)
          setConfirmationResult(result)
        })
        .catch(err => {
          setIsLoading(false)
          const msg = friendlyMessageFor(err)
          toast.error(msg, { autoClose: false })
        })
    }
  }

  const verifyCode = (code: string) => {
    if (!confirmationResult) {
      return
    }

    setIsLoading(true)

    confirmationResult
      .confirm(code)
      .then(result => {
        setIsLoading(false)

        if (mode === PhoneLoginSignupFormMode.login && phoneLoginCallback) {
          phoneLoginCallback(result.user!.uid)
        } else if (phoneSignupCallback) {
          phoneSignupCallback(
            formValues.firstName,
            formValues.lastName,
            formValues.phonenumber
          )
        }
      })
      .catch(err => {
        setIsLoading(false)
        const msg = friendlyMessageFor(err)
        toast.error(msg, { autoClose: false })
      })
  }

  if (confirmationResult) {
    return (
      <>
        <h3 className="mt-3">Verify your phonenumber</h3>
        <p className="text-dark mt-4 mb-1">Enter the 6-digit code we sent to</p>
        <h4>{formValues.phonenumber}</h4>
        <Formik
          validationSchema={enterCodeSchema}
          onSubmit={values => {
            verifyCode(values.code)
          }}
          initialValues={{
            code: ''
          }}
        >
          {({
            handleSubmit,
            handleChange,
            handleBlur,
            values,
            errors,
            touched
          }) => (
            <Form noValidate onSubmit={handleSubmit}>
              <Form.Row>
                <Form.Group as={Col}>
                  <Form.Label className="mochi-sign-in-label" htmlFor="code">
                    6-digit code
                  </Form.Label>
                  <Form.Control
                    id="code"
                    type="text"
                    name="code"
                    placeholder="Enter code"
                    value={values.code}
                    onBlur={handleBlur}
                    isInvalid={touched.code && !!errors.code}
                    onChange={handleChange}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.code}
                  </Form.Control.Feedback>
                </Form.Group>
              </Form.Row>
              <div className="text-center mt-4 mb-4">
                <Button
                  variant="light"
                  className="mr-3"
                  onClick={() => {
                    setConfirmationResult(undefined)
                  }}
                  data-testid="backButton"
                  disabled={isLoading}
                >
                  Cancel
                </Button>
                <Button
                  variant="secondary"
                  type="submit"
                  data-testid="nextButton"
                  disabled={isLoading}
                >
                  {isLoading && (
                    <span className="mr-2">
                      <Spinner animation="border" size="sm" />
                    </span>
                  )}
                  {isLoading ? 'Verifying...' : 'Submit'}
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      </>
    )
  } else if (mode === PhoneLoginSignupFormMode.login) {
    return (
      <Formik
        onSubmit={values => {
          setFormValues(values)
          verifyPhone(values.phonenumber)
        }}
        initialValues={formValues}
      >
        {({
          handleSubmit,
          values,
          touched,
          setFieldValue,
          setFieldTouched
        }) => (
          <Form noValidate onSubmit={handleSubmit}>
            <Form.Group>
              <Form.Label
                className="mochi-sign-in-label"
                htmlFor="signup-phonenumber"
              >
                Phone number
              </Form.Label>
              <PhoneInput
                id="signup-phonenumber"
                international
                defaultCountry="SG"
                value={values.phonenumber}
                className={
                  'form-control' +
                  (touched.phonenumber ? ' touched' : '') +
                  (isValidNumber ? ' valid' : ' invalid')
                }
                onChange={number => {
                  setFieldValue('phonenumber', number)
                  setFieldTouched('phonenumber')
                  setIsValidNumber(isPossiblePhoneNumber(number))
                }}
              />
              {touched.phonenumber && !isValidNumber && (
                <div className="invalid-feedback" style={{ display: 'block' }}>
                  {msg.auth.invalidPhonenumber}
                </div>
              )}
            </Form.Group>
            <div className="text-center mt-4">
              <Button
                variant="secondary"
                type="submit"
                data-testid="login-submit-button"
                disabled={isLoading || !isValidNumber}
                id="submitButton"
              >
                {isLoading && (
                  <span className="mr-2">
                    <Spinner animation="border" size="sm" />
                  </span>
                )}
                {isLoading ? 'Signing in...' : 'Sign in'}
              </Button>
            </div>
            {uiConfig && <SignupFormSocialButtons uiConfig={uiConfig} />}
          </Form>
        )}
      </Formik>
    )
  } else {
    return (
      <Formik
        validationSchema={signupSchema}
        onSubmit={values => {
          setFormValues(values)
          verifyPhone(values.phonenumber)
        }}
        initialValues={formValues}
      >
        {({
          isValid,
          handleSubmit,
          handleBlur,
          values,
          errors,
          touched,
          setFieldValue,
          setFieldTouched
        }) => (
          <Form noValidate onSubmit={handleSubmit}>
            <Form.Row>
              <Form.Group as={Col}>
                <Form.Label className="mochi-sign-in-label" htmlFor="code">
                  First Name
                </Form.Label>
                <Form.Control
                  id="code"
                  type="text"
                  name="firstName"
                  placeholder="Enter first name"
                  value={values.firstName}
                  onBlur={handleBlur}
                  isInvalid={touched.firstName && !!errors.firstName}
                  isValid={touched.firstName && !errors.firstName}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const fName = toPersonName(e.target.value)
                    setFieldValue('firstName', fName)
                  }}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.firstName}
                </Form.Control.Feedback>
              </Form.Group>
              <Form.Group as={Col}>
                <Form.Label
                  className="mochi-sign-in-label"
                  htmlFor="signup-last-name"
                >
                  Last Name
                </Form.Label>
                <Form.Control
                  id="signup-last-name"
                  type="text"
                  name="lastName"
                  placeholder="Enter last name"
                  value={values.lastName}
                  onBlur={handleBlur}
                  isInvalid={touched.lastName && !!errors.lastName}
                  isValid={touched.lastName && !errors.lastName}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const lName = toPersonName(e.target.value)
                    setFieldValue('lastName', lName)
                  }}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.lastName}
                </Form.Control.Feedback>
              </Form.Group>
            </Form.Row>
            <Form.Group>
              <Form.Label
                className="mochi-sign-in-label"
                htmlFor="signup-phonenumber"
              >
                Phone number
              </Form.Label>
              <PhoneInput
                id="signup-phonenumber"
                international
                defaultCountry="SG"
                value={values.phonenumber}
                className={
                  'form-control' +
                  (touched.phonenumber ? ' touched' : '') +
                  (isValidNumber ? ' valid' : ' invalid')
                }
                onChange={number => {
                  setFieldValue('phonenumber', number)
                  setFieldTouched('phonenumber')
                  setIsValidNumber(isPossiblePhoneNumber(number))
                }}
              />
              {touched.phonenumber && !isValidNumber && (
                <div className="invalid-feedback" style={{ display: 'block' }}>
                  {msg.auth.invalidPhonenumber}
                </div>
              )}
            </Form.Group>
            <div className="text-center mt-4">
              <Button
                variant="secondary"
                type="submit"
                data-testid="signup-submit-button"
                disabled={!isValid || isLoading || !isValidNumber}
                id="submitButton"
              >
                {isLoading && (
                  <span className="mr-2">
                    <Spinner animation="border" size="sm" />
                  </span>
                )}
                {isLoading ? 'Signing up...' : 'Sign up'}
              </Button>
            </div>
            {uiConfig && <SignupFormSocialButtons uiConfig={uiConfig} />}
          </Form>
        )}
      </Formik>
    )
  }
}

export default PhoneLoginSignupForm
